// =============================================================================
// Shop filtering, sorting and pagination.
//
// PHP renders every card it has, each one carrying its category, platform
// family, price and release date as `data-*` attributes. This module reads those
// once, then hides, reveals and reorders the existing nodes — it never rebuilds
// them. That keeps the cards as server-rendered, escaped markup, keeps images
// that are already loading, and means a visitor without JavaScript sees the
// whole listing rather than an empty grid.
//
// The design works this way too, which is what makes a facet link such as
// `shop?category=ps5` feel instant instead of round-tripping the server.
// =============================================================================

import { str } from "./config.js";
import { closeOnEscape, lockScroll, releaseScroll } from "./overlay.js";

/** Sort that means "leave the order PHP sent". */
const FEATURED = "featured";

/** Page size used when the grid carries no usable `data-per-page`. */
const DEFAULT_PER_PAGE = 12;

/**
 * Comparators keyed by the `<select>`'s option values.
 *
 * `Array.prototype.sort` is stable, so equal prices or two products with no
 * release date keep the order the server chose.
 */
const SORTERS = {
	"price-asc": (a, b) => a.price - b.price,
	"price-desc": (a, b) => b.price - a.price,
	newest: (a, b) => b.release - a.release,
	name: (a, b) => a.title.localeCompare(b.title),
};

export default function initShopFilters() {
	document.querySelectorAll("[data-shop]").forEach(setupShop);
}

/** @param {HTMLElement} shop A `[data-shop]` grid block. */
function setupShop(shop) {
	const grid = shop.querySelector(".product-grid");

	if (!grid) {
		return;
	}

	const countEl = shop.querySelector("[data-result-count]");
	const emptyEl = shop.querySelector("[data-empty-state]");
	const loadWrap = shop.querySelector("[data-load-more-wrap]");
	const loadMore = shop.querySelector("[data-load-more]");
	const sortEl = shop.querySelector("[data-sort]");
	const minEl = shop.querySelector("[data-min-price]");
	const maxEl = shop.querySelector("[data-max-price]");
	const clearEl = shop.querySelector("[data-clear-filters]");

	const perPage = readPerPage(shop);

	// Without a load-more button there is no way to reach a second page, so
	// pagination has to be off rather than silently hiding products nobody can
	// ask for.
	const paginates = Boolean(loadWrap && loadMore);
	const firstPage = paginates ? perPage : Number.POSITIVE_INFINITY;

	/**
	 * One record per card, read once at setup.
	 *
	 * Sorting would otherwise re-query the DOM on every comparison, which is
	 * O(n log n) lookups for values that cannot change.
	 */
	const entries = [...grid.querySelectorAll(".product-card")].map((card) => ({
		card,
		category: card.dataset.category || "",
		platform: card.dataset.platform || "",
		price: Number(card.dataset.price),
		release: readRelease(card),
		title: (card.querySelector(".product-title")?.textContent || "").trim(),
	}));

	const categoryBoxes = boxes(shop, "category");
	const platformBoxes = boxes(shop, "platform");

	const state = {
		category: [],
		platform: [],
		minPrice: null,
		maxPrice: null,
		sort: sortEl ? sortEl.value : FEATURED,
		visible: firstPage,
	};

	// A facet link arrives with its box already meant to be ticked. Only honoured
	// when a matching box exists: filtering on a slug nothing on the page
	// represents would empty the grid with no visible reason and no way back.
	const requested = new URLSearchParams(window.location.search).get("category");

	if (requested && categoryBoxes.some((box) => box.value === requested)) {
		state.category = [requested];
	}

	categoryBoxes.forEach((box) => {
		box.checked = state.category.includes(box.value);
		box.addEventListener("change", () => {
			state.category = checked(categoryBoxes);
			resetPage();
			render();
		});
	});

	platformBoxes.forEach((box) => {
		box.addEventListener("change", () => {
			state.platform = checked(platformBoxes);
			resetPage();
			render();
		});
	});

	if (sortEl) {
		sortEl.addEventListener("change", () => {
			state.sort = sortEl.value;

			// Deliberately not `resetPage()`: re-sorting is a new view of the same
			// results, and collapsing back to page one would throw away products
			// the visitor had already loaded.
			render();
		});
	}

	if (minEl) {
		minEl.addEventListener("input", () => {
			state.minPrice = readBound(minEl.value);
			resetPage();
			render();
		});
	}

	if (maxEl) {
		maxEl.addEventListener("input", () => {
			state.maxPrice = readBound(maxEl.value);
			resetPage();
			render();
		});
	}

	if (clearEl) {
		clearEl.addEventListener("click", () => {
			[...categoryBoxes, ...platformBoxes].forEach((box) => {
				box.checked = false;
			});

			if (minEl) {
				minEl.value = "";
			}

			if (maxEl) {
				maxEl.value = "";
			}

			state.category = [];
			state.platform = [];
			state.minPrice = null;
			state.maxPrice = null;

			// Sorting is not a filter, so it survives a clear.
			resetPage();
			render();
		});
	}

	if (loadMore) {
		loadMore.addEventListener("click", () => {
			state.visible += perPage;
			render();
		});
	}

	function resetPage() {
		state.visible = firstPage;
	}

	function matches(entry) {
		if (state.category.length > 0 && !state.category.includes(entry.category)) {
			return false;
		}

		if (state.platform.length > 0 && !state.platform.includes(entry.platform)) {
			return false;
		}

		// An unparseable price fails both comparisons, so a card missing one is
		// kept rather than quietly excluded by a price filter.
		if (state.minPrice !== null && entry.price < state.minPrice) {
			return false;
		}

		if (state.maxPrice !== null && entry.price > state.maxPrice) {
			return false;
		}

		return true;
	}

	function render() {
		const matched = entries.filter(matches);
		const sorter = Object.prototype.hasOwnProperty.call(SORTERS, state.sort)
			? SORTERS[state.sort]
			: null;

		// `matched` is already in server order, so "featured" needs no work.
		const ordered = sorter ? [...matched].sort(sorter) : matched;

		// Moving the nodes rather than replacing them: appending an element that
		// is already in the grid relocates it, which is the whole reorder.
		ordered.forEach(({ card }) => grid.append(card));

		const position = new Map(ordered.map((entry, index) => [entry.card, index]));

		entries.forEach(({ card }) => {
			const index = position.get(card);
			card.hidden = index === undefined || index >= state.visible;
		});

		const total = ordered.length;

		if (countEl) {
			countEl.textContent = countText(total);
		}

		// The grid goes too, so an empty result reads as one message rather than
		// a notice above a stretch of blank track.
		grid.hidden = total === 0;

		if (emptyEl) {
			emptyEl.hidden = total !== 0;
		}

		if (loadWrap) {
			loadWrap.hidden = total <= state.visible;
		}
	}

	initDrawer(shop);
	render();
}

/**
 * The result count, worded the way PHP renders it.
 *
 * @param {number} total Matching products.
 * @return {string}
 */
function countText(total) {
	const format =
		total === 1 ? str("countOne", "%s product") : str("countMany", "%s products");

	return format.replace("%s", total.toLocaleString());
}

/**
 * The mobile filter drawer.
 *
 * Same shape as the navigation drawer — a class pair, a click-away backdrop, and
 * the shared scroll lock. The lock is counted, so closing this while the
 * navigation drawer is still open cannot unlock the page underneath it.
 *
 * @param {HTMLElement} shop A `[data-shop]` grid block.
 */
function initDrawer(shop) {
	const toggle = shop.querySelector("[data-filter-toggle]");
	const panel = shop.querySelector("[data-filters]");
	const backdrop = shop.querySelector("[data-filter-backdrop]");

	if (!toggle || !panel) {
		return;
	}

	let open = false;

	const setOpen = (next) => {
		if (next === open) {
			return;
		}

		open = next;

		panel.classList.toggle("is-open", open);
		toggle.setAttribute("aria-expanded", open ? "true" : "false");

		if (backdrop) {
			backdrop.classList.toggle("is-open", open);
		}

		if (open) {
			lockScroll();
		} else {
			releaseScroll();
		}
	};

	toggle.addEventListener("click", () => setOpen(!open));

	if (backdrop) {
		backdrop.addEventListener("click", () => setOpen(false));
	}

	closeOnEscape(() => setOpen(false));
}

/**
 * The checkbox group for one facet.
 *
 * @param {HTMLElement} shop  A `[data-shop]` grid block.
 * @param {string}      facet `category` or `platform`.
 * @return {HTMLInputElement[]}
 */
function boxes(shop, facet) {
	return [...shop.querySelectorAll(`[data-filter="${facet}"]`)];
}

/** @param {HTMLInputElement[]} list @return {string[]} */
function checked(list) {
	return list.filter((box) => box.checked).map((box) => box.value);
}

/**
 * Page size from the grid's own markup, so the block's `perPage` attribute stays
 * the single source of truth.
 *
 * @param {HTMLElement} shop A `[data-shop]` grid block.
 * @return {number}
 */
function readPerPage(shop) {
	const parsed = Number(shop.dataset.perPage);

	return Number.isFinite(parsed) && parsed >= 1 ? Math.floor(parsed) : DEFAULT_PER_PAGE;
}

/**
 * A card's release date as a timestamp.
 *
 * Products with no date — anything that is not a pre-order, usually — sort as
 * epoch, which puts them last under "Newest Release" instead of scattering them
 * the way a NaN comparator would.
 *
 * @param {HTMLElement} card A `.product-card`.
 * @return {number}
 */
function readRelease(card) {
	const parsed = Date.parse(card.dataset.release || "");

	return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * A price bound from a `type="number"` field.
 *
 * @param {string} value The field's current value.
 * @return {number|null} Null when the field is blank or unreadable.
 */
function readBound(value) {
	if (value === "") {
		return null;
	}

	const parsed = Number(value);

	return Number.isFinite(parsed) ? parsed : null;
}
